import React, { useEffect } from 'react';
import {
  Body,
  Box,
  Button,
  H4,
  Modal,
  Stack,
  notifications,
  useDisclosure,
  useWindowProperties,
} from '@qred/components-library';
import { ApiStatus, Language } from '~/store/types/sharedTypes';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '~/hooks/useAppDispatch';
import useQueryParams from '~/hooks/useQueryParams';
import {
  initiateOTP,
  verifyOTP,
} from '~/store/actions/additionalSignerActions';
import AdditionalSignerOTPTimer from './AdditionalSignerOTPTimer';
import AdditionalSignerOTPErrorModal from './AdditionalSignerOTPErrorModal';
import { Redirect } from 'react-router';
import constVars from '~/constants/constVars';
import { getCookieValue } from '~/helpers/utils';
import { CountryCode, OTPErrorType, ProductType } from '~/enums';
import {
  setApiStatus,
  setOTPError,
} from '~/store/slices/additionalSigner.slice';
import useTranslate from '~/hooks/useTranslate';
import SpinnerCircular from '~/components/shared/Spinner/SpinnerCircular';
import { parseLanguage } from '~/helpers/market.helper';
import { setLanguage } from '~/store/slices/intl.slice';
import AdditionalSignerOTPInputSection from './AdditionalSignerOTPInputSection';
import { timestampIsInThePast } from '~/helpers/date.helper';
import { RootState } from '~/store';

const AdditionalSignerOTP = () => {
  const {
    intl: { market, languageIsSet },
    additionalSigner: { otp, apiStatus, otpExpiryTimestamp, otpError },
  } = useSelector((state: RootState) => state);

  const queryParams = useQueryParams();
  const hashCode = queryParams.get('hashCode');
  const signerId = queryParams.get('signerId');
  const productType = queryParams.get('product');
  const languageParam = queryParams.get('lang');

  const dispatch = useAppDispatch();
  const translate = useTranslate();
  const [
    opened,
    { open: openAdditionalSignerOTPErrorModal, close },
  ] = useDisclosure(false);
  const { isMobile } = useWindowProperties();

  useEffect(() => {
    if (languageParam) {
      let parsedLanguage = parseLanguage(queryParams.get('lang'));
      // Dutch language is not supported in Belgium in our code, so we need to change it to DutchBE
      if (parsedLanguage === Language.Dutch && market === CountryCode.BE) {
        parsedLanguage = Language.DutchBE;
      }
      parsedLanguage && dispatch(setLanguage(parsedLanguage));
    }
    document.cookie = `additionalSignerHashCode=${hashCode}; Path=/; Max-Age=86400`;
    document.cookie = `additionalSignerId=${signerId}; Path=/; Max-Age=86400`;
    document.cookie = `additionalSignerProductType=${productType}; Path=/; Max-Age=86400`;
  }, []);

  const isValidOtpLength = otp.length === 6;
  const otpIsExpired = timestampIsInThePast(otpExpiryTimestamp);

  const additionalSignerAccessToken = getCookieValue(
    'additionalSignerAccessToken'
  );

  useEffect(() => {
    const urlParametersAreValidAndUserDoesNotHaveAToken =
      hashCode && signerId && productType && !additionalSignerAccessToken;

    if (urlParametersAreValidAndUserDoesNotHaveAToken) {
      dispatch(initiateOTP(market));
    }
  }, []);

  useEffect(() => {
    if (apiStatus.regenerateOTP === ApiStatus.Completed) {
      notifications.show({
        state: 'success',
        message: translate('AdditionalSigner.CodeSent'),
      });
      dispatch(setApiStatus({ regenerateOTP: ApiStatus.Idle }));
    }
  }, [apiStatus]);

  useEffect(() => {
    if (otpError === OTPErrorType.UNKNOWN) {
      dispatch(setOTPError(null));
      openAdditionalSignerOTPErrorModal(); // generic error popup
    }
  }, [otpError]);

  const onClickVerifyOTP = () => {
    if (signerId && hashCode && isValidOtpLength) {
      dispatch(verifyOTP(market, otp));
    }
  };

  if (otpError === OTPErrorType.SIGNER_ALREADY_SIGNED) {
    return <Redirect to={constVars.ROUTE_SIGNER_ERROR} />;
  }

  if (additionalSignerAccessToken) {
    if (productType === ProductType.CARD) {
      return <Redirect to={constVars.ROUTE_CARD_ADDITIONAL_SIGNER} />;
    } else if (productType === ProductType.LOAN) {
      return <Redirect to={constVars.ROUTE_LOAN_ADDITIONAL_SIGNER} />;
    }
  }

  return languageIsSet ? (
    <>
      <Box p={'xxl'}>
        <Stack h={400} spacing={'xxl'}>
          <Stack spacing={'sm'}>
            <H4>{translate('AdditionalSigner.EnterOtp')}</H4>
            <Body size="lg">{translate('AdditionalSigner.OtpTitle')}</Body>
          </Stack>
          <Stack spacing={'xs'}>
            <AdditionalSignerOTPInputSection />
            {otpExpiryTimestamp && !otpIsExpired && (
              <AdditionalSignerOTPTimer
                otpExpiryTimestamp={otpExpiryTimestamp}
              />
            )}
          </Stack>
        </Stack>
      </Box>
      <Box bg={'neutral.10'} p={'lg'} display={'flex'}>
        <Button
          onClick={onClickVerifyOTP}
          size="md"
          fullWidth={isMobile}
          disabled={!isValidOtpLength || otpIsExpired || !otpExpiryTimestamp}
          isLoading={apiStatus.verifyOTP === ApiStatus.Started}
          ml={'auto'}
        >
          {translate('AdditionalSigner.VerifyButton')}
        </Button>
      </Box>

      <Modal
        id="otp-something-went-wrong-modal"
        opened={opened}
        onClose={close}
        size="md"
        title={translate('AdditionalSigner.ErrorTitle')}
      >
        <AdditionalSignerOTPErrorModal onClose={close} />
      </Modal>
    </>
  ) : (
    <SpinnerCircular />
  );
};

export default AdditionalSignerOTP;
